import type {
    SketchConstraintFormulaCompileContext,
    SketchConstraintFormulaCompileResult,
    SketchConstraintFormulaCompiler,
} from '../../../../types/sketch/solver/SketchConstraintFormulaCompiler';
import type {
    SketchParametricPointHandle,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SketchParametricEquationFactory} from './SketchParametricEquationFactory';

/**
 * 直线方向与交点关系公式编译器。
 *
 * 本类只把关系领域状态翻译为残差方程，不选择运行时移动侧、不直接修改草图对象。
 * 直线相关公式统一按无限直线语义计算，与 SOLIDWORKS 行为一致。
 */
export class SketchLineConstraintFormulaCompiler implements SketchConstraintFormulaCompiler {
    /** 当前已从旧大 switch 迁移的关系类型。 */
    public readonly relationKinds = [
        'Horizontal',
        'Vertical',
        'Parallel',
        'Perpendicular',
        'Collinear',
        'Intersection',
        'Coincident',
    ] as const;

    /**
     * 编译直线关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    public compile(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        switch (context.constraintState.relationKind) {
            case 'Horizontal':
                return this.compileDirectional(context, 'horizontal');
            case 'Vertical':
                return this.compileDirectional(context, 'vertical');
            case 'Parallel':
                return this.compileLinePairDirection(context, 'parallel');
            case 'Perpendicular':
                return this.compileLinePairDirection(context, 'perpendicular');
            case 'Collinear':
                return this.compileCollinear(context);
            case 'Intersection':
                return this.compileIntersection(context);
            case 'Coincident':
                return this.compileLineCoincident(context);
            default:
                return 'not-applicable';
        }
    }

    /**
     * 编译水平或竖直关系。
     *
     * @param context 编译上下文。
     * @param direction 方向关系类型。
     * @returns 编译结果。
     */
    private compileDirectional(
        context: SketchConstraintFormulaCompileContext,
        direction: 'horizontal' | 'vertical',
    ): SketchConstraintFormulaCompileResult {
        const directEntityIds = context.resolveDirectEntityIds();
        const directLines = this.resolveDirectLines(context);
        const directPoints = context.resolveDirectPointHandles();

        if (directEntityIds.length > 0 && directLines.length === directEntityIds.length) {
            directLines.forEach((line, index) => {
                const equationId = `constraint:${context.constraintState.constraintId}:line:${index}`;
                const equation = direction === 'horizontal'
                    ? SketchParametricEquationFactory.horizontal(equationId, line[0], line[1])
                    : SketchParametricEquationFactory.vertical(equationId, line[0], line[1]);

                context.system.addEquation(equation);
            });
            return 'compiled';
        }

        if (directPoints.length >= 2 && directEntityIds.length === 0) {
            const anchor = directPoints[0]!;

            directPoints.slice(1).forEach((point, index) => {
                const equationId = `constraint:${context.constraintState.constraintId}:point:${index}`;
                const equation = direction === 'horizontal'
                    ? SketchParametricEquationFactory.horizontal(equationId, anchor, point)
                    : SketchParametricEquationFactory.vertical(equationId, anchor, point);

                context.system.addEquation(equation);
            });
            return 'compiled';
        }

        context.reportUnsupported(`${direction === 'horizontal' ? '水平' : '竖直'}关系目标无法完整解析为直线集合或点集合`);
        return 'rejected';
    }

    /**
     * 编译两直线平行或垂直关系。
     *
     * @param context 编译上下文。
     * @param direction 线对方向关系。
     * @returns 编译结果。
     */
    private compileLinePairDirection(
        context: SketchConstraintFormulaCompileContext,
        direction: 'parallel' | 'perpendicular',
    ): SketchConstraintFormulaCompileResult {
        const directEntityIds = context.resolveDirectEntityIds();
        const lines = this.resolveDirectLines(context);

        if (lines.length < 2 || lines.length !== directEntityIds.length) {
            context.reportUnsupported(`${direction === 'parallel' ? '平行' : '垂直'}关系需要两个以上可解析线实体目标`);
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const anchor = lines[0]!;

        lines.slice(1).forEach((line, index) => {
            const pairEquationId = `${equationId}:pair:${index}`;
            const equation = direction === 'parallel'
                ? SketchParametricEquationFactory.parallel(
                    pairEquationId,
                    anchor[0],
                    anchor[1],
                    line[0],
                    line[1],
                )
                : SketchParametricEquationFactory.perpendicular(
                    pairEquationId,
                    anchor[0],
                    anchor[1],
                    line[0],
                    line[1],
                );

            context.system.addEquation(equation);
        });
        return 'compiled';
    }

    /**
     * 编译两直线共线关系。
     *
     * 共线由“方向平行 + 第二线起点位于第一条无限直线”两组残差共同表达。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileCollinear(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const directEntityIds = context.resolveDirectEntityIds();
        const lines = this.resolveDirectLines(context);

        if (lines.length < 2 || lines.length !== directEntityIds.length) {
            context.reportUnsupported('共线关系需要两个以上可解析线实体目标');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;
        const anchor = lines[0]!;

        lines.slice(1).forEach((line, index) => {
            context.system.addEquation(SketchParametricEquationFactory.parallel(
                `${equationId}:parallel:${index}`,
                anchor[0],
                anchor[1],
                line[0],
                line[1],
            ));
            context.system.addEquation(SketchParametricEquationFactory.pointOnLine(
                `${equationId}:on-line:${index}`,
                line[0],
                anchor[0],
                anchor[1],
            ));
        });

        return 'compiled';
    }

    /**
     * 编译两条无限直线的交点关系。
     *
     * @param context 编译上下文。
     * @returns 编译结果。
     */
    private compileIntersection(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const linePair = this.resolveDirectLinePair(context);
        const focusPoint = context.resolveDirectPointHandles()[0];

        if (!linePair || !focusPoint) {
            context.reportUnsupported('交叉点关系需要两条线与一个焦点目标');
            return 'rejected';
        }

        const equationId = `constraint:${context.constraintState.constraintId}`;

        context.system.addEquation(SketchParametricEquationFactory.pointOnLine(
            `${equationId}:on-first`,
            focusPoint,
            linePair[0][0],
            linePair[0][1],
        ));
        context.system.addEquation(SketchParametricEquationFactory.pointOnLine(
            `${equationId}:on-second`,
            focusPoint,
            linePair[1][0],
            linePair[1][1],
        ));

        return 'compiled';
    }

    /**
     * 编译两直线端点重合关系。
     *
     * 旧对象式 `LineCoincidentConstraint` 的可观察语义是：在两条线的四组端点组合中，
     * 固定当前几何最近的一对并令其重合。参数方程必须在编译期锁定该分支，禁止每次数值
     * 迭代重新选择最近端点，否则端点分支会在等距附近跳变并导致残差不连续。
     *
     * @param context 编译上下文。
     * @returns 目标为两条直线时返回编译结果；其他 Coincident 组合交给点关系编译器。
     */
    private compileLineCoincident(
        context: SketchConstraintFormulaCompileContext,
    ): SketchConstraintFormulaCompileResult {
        const linePair = this.resolveDirectLinePair(context);

        if (!linePair) {
            return 'not-applicable';
        }

        const candidates = [
            [linePair[0][0], linePair[1][0]],
            [linePair[0][0], linePair[1][1]],
            [linePair[0][1], linePair[1][0]],
            [linePair[0][1], linePair[1][1]],
        ] as const;
        let selected = candidates[0];
        let selectedDistanceSquared = Number.POSITIVE_INFINITY;

        candidates.forEach((candidate) => {
            const deltaX = context.system.read(candidate[1].xId) - context.system.read(candidate[0].xId);
            const deltaY = context.system.read(candidate[1].yId) - context.system.read(candidate[0].yId);
            const distanceSquared = deltaX * deltaX + deltaY * deltaY;

            // 相等距离保持候选声明顺序，保证同一 seed 下分支选择确定。
            if (distanceSquared < selectedDistanceSquared) {
                selected = candidate;
                selectedDistanceSquared = distanceSquared;
            }
        });

        context.system.addEquation(SketchParametricEquationFactory.coincident(
            `constraint:${context.constraintState.constraintId}`,
            selected[0],
            selected[1],
        ));
        return 'compiled';
    }

    /**
     * 解析关系直接目标中的两条直线。
     *
     * 必须读取 `targetRefs` 派生的直接实体 ID，不能使用包含 island 图影响范围的点集合猜测目标。
     *
     * @param context 编译上下文。
     * @returns 两条直线的参数句柄；目标不合法时返回 null。
     */
    private resolveDirectLinePair(
        context: SketchConstraintFormulaCompileContext,
    ): readonly [
        readonly [SketchParametricPointHandle, SketchParametricPointHandle],
        readonly [SketchParametricPointHandle, SketchParametricPointHandle],
    ] | null {
        const directEntityIds = context.resolveDirectEntityIds();

        if (directEntityIds.length !== 2) {
            return null;
        }

        const firstLine = context.linePointHandles.get(directEntityIds[0] ?? '');
        const secondLine = context.linePointHandles.get(directEntityIds[1] ?? '');

        return firstLine && secondLine ? [firstLine, secondLine] : null;
    }

    /**
     * 解析关系直接目标中的全部直线，并保持目标顺序。
     *
     * @param context 编译上下文。
     * @returns 可解析的直线参数句柄；调用方必须与直接实体数量对比，防止部分编译。
     */
    private resolveDirectLines(
        context: SketchConstraintFormulaCompileContext,
    ): readonly (readonly [SketchParametricPointHandle, SketchParametricPointHandle])[] {
        return context.resolveDirectEntityIds()
            .map((entityId) => context.linePointHandles.get(entityId))
            .filter((line): line is readonly [SketchParametricPointHandle, SketchParametricPointHandle] => (
                Boolean(line)
            ));
    }
}
